import {
  Box,
  Typography,
  Divider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import { format } from 'date-fns';
import { useState } from 'react';
import {
  NoteDto,
  SampleActivityDto,
  SampleDto,
  VisibilityDto,
  ProjectDto,
  DataPackageDto,
  ProjectActivityDto,
  TheoryActivityDto,
} from '@/api/ApiClient';
import { useAuthStore } from '@/stores/authStore';
import { RichTextEditor } from '@/components/common/RichTextEditor';
import { getVisibilityTypeDisplay, VISIBILITY_OPTIONS } from '@/lib/types/visibilityTypes';

import { useBackendStore } from '@/stores/backendStore';
import { log } from '@/lib/utils/logger';
import { useSnackbar } from '@/hooks/useSnackbar';
import { TreeNode } from '@/types/tree';
import { useTree } from '@/hooks/useTree';
import { useUserResolution } from '@/lib/options/useUserResolution';

import { nodeIsReadOnlyForUi } from '@/lib/tree/treeNodeTypeConfigs';
import { usePrivileges } from '@/hooks/usePrivileges';
import { canCreateNote, canUpdateNote, canDeleteNote } from '@/types/tree/actions/noteFilePrivilegeHelpers';
import { getProblemDetail } from '@/lib/utils/apiErrors';

type NotesNode = TreeNode;

export const NotesDetails = ({ notes: initialNotes, node }: { notes: NoteDto[]; node: NotesNode }) => {
  const [showNewNote, setShowNewNote] = useState(false);
  const { resolveUser } = useUserResolution();
  const { isAuthorized } = usePrivileges();

  const currentUser = useAuthStore((s) => s.currentUser?.id);

  // Note privilege checks
  const canCreate = canCreateNote(isAuthorized);
  const backend = useBackendStore((state) => state.getBackend());
  const { showSnackbar } = useSnackbar();
  const { reloadNode, getTypedData } = useTree();
  const isReadOnlyNode = nodeIsReadOnlyForUi(node.type);
  const [isSaving, setIsSaving] = useState(false);
  const [newNote, setNewNote] = useState<NoteDto>({
    title: '',
    text: '',
    visibility: VisibilityDto.Internal,
    lastChangedDate: new Date(),
    lastChangedByUser: currentUser,
    status: undefined,
  });

  const [editedNote, setEditedNote] = useState<NoteDto | null>(null);

  // Get node data for use throughout the component
  const nodeData = getTypedData(node);

  // Centralized configuration for all note operations
  const noteOperationConfig = {
    sample: {
      getData: () => getTypedData(node) as SampleDto,
      errorMessage: 'Sample not found',
      saveNotes: async (notes: NoteDto[]) => backend.sample.saveNotes((nodeData as SampleDto)?.sampleLabel!, notes),
      deleteNote: async (noteId: number) => backend.sample.deleteNote((nodeData as SampleDto)?.sampleLabel!, noteId),
    },
    sampleActivity: {
      getData: () => getTypedData(node) as SampleActivityDto,
      errorMessage: 'Sample activity not found',
      saveNotes: async (notes: NoteDto[]) =>
        backend.sampleActivity.saveNotes((nodeData as SampleActivityDto)?.id!, notes),
      deleteNote: async (noteId: number) =>
        backend.sampleActivity.deleteNote((nodeData as SampleActivityDto)?.id!, noteId),
    },
    project: {
      getData: () => getTypedData(node) as ProjectDto,
      errorMessage: 'Project not found',
      saveNotes: async (notes: NoteDto[]) => {
        await backend.project.saveNotes((nodeData as ProjectDto)?.id!.toString(), notes);
      },
      deleteNote: async (noteId: number) => {
        await backend.project.deleteNote((nodeData as ProjectDto)?.id!.toString(), noteId);
      },
    },
    projectActivity: {
      getData: () => getTypedData(node) as ProjectActivityDto,
      errorMessage: 'Project activity not found',
      saveNotes: async (notes: NoteDto[]) =>
        backend.projectActivity.saveNotes((nodeData as ProjectActivityDto)?.id!, notes),
      deleteNote: async (noteId: number) =>
        backend.projectActivity.deleteNote((nodeData as ProjectActivityDto)?.id!, noteId),
    },
    theoryActivity: {
      getData: () => getTypedData(node) as TheoryActivityDto,
      errorMessage: 'Theory activity not found',
      saveNotes: async (notes: NoteDto[]) => {
        await backend.theoryActivity.saveNotes((nodeData as TheoryActivityDto)?.id!, notes);
      },
      deleteNote: async (noteId: number) => {
        await backend.theoryActivity.deleteNote((nodeData as TheoryActivityDto)?.id!, noteId);
      },
    },
    dataPackage: {
      getData: () => getTypedData(node) as DataPackageDto,
      errorMessage: 'Data package not found',
      saveNotes: async (notes: NoteDto[]) => {
        await backend.data.saveNotes((nodeData as DataPackageDto)?.id!.toString(), notes);
      },
      deleteNote: async (noteId: number) => {
        await backend.data.deleteNote((nodeData as DataPackageDto)?.id!.toString(), noteId);
      },
    },
    simulationDataset: {
      getData: () => getTypedData(node) as DataPackageDto,
      errorMessage: 'Simulation dataset not found',
      saveNotes: async (notes: NoteDto[]) => {
        await backend.data.saveNotes((nodeData as DataPackageDto)?.id!.toString(), notes);
      },
      deleteNote: async (noteId: number) => {
        await backend.data.deleteNote((nodeData as DataPackageDto)?.id!.toString(), noteId);
      },
    },
  };

  // Helper function to get the appropriate config based on node type
  const getNoteConfig = () => {
    const config = noteOperationConfig[node.type as keyof typeof noteOperationConfig];
    if (!config) {
      throw new Error(
        `NotesDetails: Node type '${node.type}' does not support notes. This component should not be rendered for this node type.`
      );
    }
    return config;
  };

  // Check if the node is a sample activity and if it is submitted
  const handleEditNote = (note: NoteDto) => {
    setEditedNote({ ...note });
  };

  const saveNotes = async (notes: NoteDto[]) => {
    const config = getNoteConfig();
    await config.saveNotes(notes);
  };

  const handleUpdateNote = async () => {
    if (!editedNote?.id) return;

    setIsSaving(true);
    try {
      const payload = [
        {
          ...editedNote,
          lastChangedDate: new Date(),
          lastChangedByUser: currentUser,
        },
      ];
      await saveNotes(payload);
      await reloadNode(node.nodeId, backend);
      showSnackbar('Note updated successfully', 'success');
      setEditedNote(null);
    } catch (error) {
      log.debug('[NotesDetails] Failed to update note:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to update note.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteNote = async (noteId: number) => {
    if (!noteId) return;
    try {
      const config = getNoteConfig();
      await config.deleteNote(noteId);

      await reloadNode(node.nodeId, backend);
      showSnackbar('Note deleted successfully', 'success');
    } catch (error) {
      log.debug('[NotesDetails] Failed to delete note:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to delete note.', 'error');
    }
  };

  const handleSaveNote = async () => {
    setIsSaving(true);
    try {
      const payload = [
        {
          ...newNote,
          lastChangedDate: new Date(),
          lastChangedByUser: currentUser,
        },
      ];

      await saveNotes(payload);
      await reloadNode(node.nodeId, backend);

      setNewNote({
        title: '',
        text: '',
        visibility: VisibilityDto.Internal,
        lastChangedDate: new Date(),
        lastChangedByUser: currentUser,
        status: undefined,
      });
      setShowNewNote(false);
      showSnackbar('Added note successfully', 'success');
    } catch (error) {
      log.debug('[NotesDetails] Failed to save note:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to add note.', 'error');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Notes</Typography>
      </Box>

      {showNewNote ? (
        <Accordion expanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight={600}>New Note</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Title"
                value={newNote.title}
                onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                fullWidth
                margin="normal"
              />

              <TextField
                label="Created On"
                value={format(newNote.lastChangedDate ?? new Date(), 'PPpp')}
                inputProps={{ readOnly: true }}
                fullWidth
                margin="normal"
              />

              <FormControl fullWidth margin="normal">
                <InputLabel id="visibility-label">Visibility</InputLabel>
                <Select
                  value={newNote.visibility || ''}
                  onChange={(e) => setNewNote({ ...newNote, visibility: e.target.value })}
                  label="Visibility"
                  labelId="visibility-label"
                  fullWidth
                >
                  {VISIBILITY_OPTIONS.map((value) => (
                    <MenuItem key={value} value={value}>
                      {getVisibilityTypeDisplay(value)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <RichTextEditor value={newNote.text || ''} onChange={(html) => setNewNote({ ...newNote, text: html })} />

              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button onClick={() => setShowNewNote(false)}>Cancel</Button>
                <Button onClick={handleSaveNote} variant="contained" disabled={!newNote.text}>
                  Save Note
                </Button>
              </Box>
            </Box>
          </AccordionDetails>
        </Accordion>
      ) : null}

      {initialNotes.length === 0 && !showNewNote ? <Typography>No notes available.</Typography> : null}

      {initialNotes.map((note, idx) => (
        <Box key={note.id ?? idx} sx={{ mb: 2, position: 'relative' }}>
          {editedNote?.id === note.id ? (
            // EDIT MODE
            <Box display="flex" flexDirection="column" gap={2}>
              <TextField
                label="Title"
                value={editedNote?.title || ''}
                onChange={(e) => setEditedNote({ ...editedNote!, title: e.target.value })}
                fullWidth
              />

              <FormControl fullWidth margin="normal">
                <InputLabel id="visibility-label">Visibility</InputLabel>
                <Select
                  value={editedNote?.visibility || ''}
                  onChange={(e) => setEditedNote({ ...editedNote!, visibility: e.target.value })}
                  label="Visibility"
                  labelId="visibility-label"
                  fullWidth
                >
                  {VISIBILITY_OPTIONS.map((value) => (
                    <MenuItem key={value} value={value}>
                      {getVisibilityTypeDisplay(value)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <RichTextEditor
                value={editedNote?.text || ''}
                onChange={(html) => setEditedNote({ ...editedNote!, text: html })}
              />

              <Box display="flex" justifyContent="flex-end" gap={2}>
                <Button
                  onClick={() => {
                    setEditedNote(null);
                  }}
                >
                  Cancel
                </Button>
                <Button variant="contained" onClick={handleUpdateNote} disabled={!editedNote?.text}>
                  Update Note
                </Button>
              </Box>
            </Box>
          ) : (
            // VIEW MODE
            <>
              {note.title ? (
                <Typography variant="subtitle1" fontWeight={600}>
                  {note.title}
                </Typography>
              ) : null}
              {note.text ? (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ whiteSpace: 'pre-line' }}
                  dangerouslySetInnerHTML={{ __html: note.text }}
                />
              ) : null}
              <Typography variant="caption" color="text.disabled">
                {note.lastChangedByUser ? `By ${resolveUser(note.lastChangedByUser)}` : null}
                &nbsp;
                {note.lastChangedDate ? `on ${format(new Date(note.lastChangedDate), 'PPpp')}` : null}
              </Typography>

              {/* ACTION ICONS */}
              {!isReadOnlyNode
                ? (() => {
                    const canUpdate = canUpdateNote(isAuthorized, note, currentUser);
                    const canDelete = canDeleteNote(isAuthorized, note, currentUser);
                    if (!canUpdate && !canDelete) {
                      return null;
                    }
                    return (
                      <Box position="absolute" top={0} right={0} display="flex" gap={1}>
                        {canUpdate ? (
                          <Tooltip title="Edit note">
                            <span>
                              <IconButton
                                onClick={() => {
                                  handleEditNote(note);
                                }}
                                size="small"
                                sx={{
                                  backgroundColor: 'primary.main',
                                  color: 'white',
                                  borderRadius: 1,
                                  width: 32,
                                  height: 32,
                                  '&:hover': {
                                    backgroundColor: 'primary.dark',
                                  },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                        {canDelete ? (
                          <Tooltip title="Delete note">
                            <span>
                              <IconButton
                                onClick={() => {
                                  handleDeleteNote(note.id!);
                                }}
                                size="small"
                                sx={{
                                  backgroundColor: 'error.main',
                                  color: 'white',
                                  borderRadius: 1,
                                  width: 32,
                                  height: 32,
                                  '&:hover': {
                                    backgroundColor: 'error.dark',
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        ) : null}
                      </Box>
                    );
                  })()
                : null}
            </>
          )}

          {idx < initialNotes.length - 1 ? <Divider sx={{ mt: 2 }} /> : null}
        </Box>
      ))}

      {/* New Note button below the list */}
      {!isReadOnlyNode && canCreate ? (
        <Box mt={2}>
          <Tooltip title="Add new note">
            <span>
              <Button variant="contained" onClick={() => setShowNewNote(!showNewNote)} startIcon={<AddIcon />}>
                New Note
              </Button>
            </span>
          </Tooltip>
        </Box>
      ) : null}

      {isSaving ? (
        <Box display="flex" justifyContent="center" alignItems="center" my={2}>
          <CircularProgress size={24} />
        </Box>
      ) : null}
    </Box>
  );
};
